import { useEffect } from 'react';
import { logEvent } from './logger-utils';
import { removeInitialLoader } from './utils';

// Preview overlay stays up until every boot placeholder is gone.
// If a chunk or API hangs, reveal anyway rather than trap the user on a spinner.
// This is a hang net, not a UX budget — see Risks for why it is not shorter.
const FAILSAFE_MS = 15_000;

let pending = 0;
let revealed = false;
let failsafe: ReturnType<typeof setTimeout> | undefined;
let frame: number | undefined;

export const revealApp = () => {
  if (revealed) return;
  revealed = true;
  clearTimeout(failsafe);
  stopRevealPolling();
  removeInitialLoader();
};

// Every real first view (the three wrapper shells, CheckoutSkeleton, GenericErrorPage)
// renders the extro-ui Header, which emits `.header-container`. A bare Suspense
// fallback or an empty root never does. NOTE: that component renders a styled DIV,
// not a <header> element — do not "simplify" this to a tag selector.
// Scoped to #root because the static preview markup contains one too.
const rootIsPainted = () => !!document.querySelector('#root .header-container');

// Poll every frame until the shell is actually on screen AND nothing reports itself
// still booting. A one-shot check (however many frames deep) samples two moments and
// reveals into any gap between placeholder phases that happens to be longer; this
// cannot, because it never stops looking until both conditions hold at once.
// Returns its own teardown so `useEffect(startRevealPolling, [])` cancels the loop —
// otherwise the rAF chain re-arms forever once #root can no longer satisfy the exit test.
export const startRevealPolling = () => {
  const tick = () => {
    if (revealed) return;
    if (pending === 0 && rootIsPainted()) {
      revealApp();
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return stopRevealPolling;
};

export const stopRevealPolling = () => {
  if (frame === undefined) return;
  cancelAnimationFrame(frame);
  frame = undefined;
};

// Bookkeeping only — the poll loop is always watching, so unmounting schedules nothing.
// `active` lets a caller hold the boot window open for a state that has no
// placeholder component of its own (see the payment-method context gate in
// paymentWrappers).
export const useBootPlaceholder = (active = true) => {
  useEffect(() => {
    if (!active) return;
    pending++;
    return () => {
      pending--;
    };
  }, [active]);
};

// Firing at all means the normal reveal path failed — log it, there is no other signal.
// Reveals even into an empty #root: a blank page can be reloaded out of, an overlay that
// never lifts (with body scroll locked) cannot.
export const armRevealFailsafe = () => {
  clearTimeout(failsafe);
  failsafe = setTimeout(() => {
    logEvent({
      message: 'Boot reveal failsafe fired',
      level: 'warning',
      extra: {
        failsafeMs: FAILSAFE_MS,
        pendingPlaceholders: pending,
        rootPainted: rootIsPainted(),
        rootHasContent: !!document.getElementById('root')?.firstChild,
        // Browsers do not run rAF in a hidden tab, so the poll loop cannot tick and the
        // failsafe is the expected outcome rather than a fault — keeps the alert filterable.
        documentHidden: document.hidden,
        fromUrl: window.location.href,
      },
    });
    revealApp();
  }, FAILSAFE_MS);
};
